import { useEffect, useState } from "react";
import { Trash2, KeyRound, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field, Panel, copyText } from "@/components/kit";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SectionHead } from "@/pages/manage/common";

export default function Admins() {
  const { user } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [a, setA] = useState({ email: "", name: "", password: "" });
  const [r, setR] = useState({ email: "", new_password: "" });
  const load = () => api.get("/platform/admins").then((x) => setAdmins(x.data));
  useEffect(() => {
    load();
  }, []);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/platform/admins", a);
      toast.success(`${a.email} is now a super-admin`);
      setA({ email: "", name: "", password: "" });
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };
  const remove = async (x) => {
    if (!window.confirm(`Remove super-admin access for ${x.email}?`)) return;
    try {
      await api.delete(`/platform/admins/${x.id}`);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };
  const reset = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/platform/users/reset-password", r);
      if (data.mode === "set") toast.success("Password updated");
      else if (data.email_status === "sent") toast.success("Reset link emailed");
      else { copyText(data.reset_url, "Email isn't set up — reset link copied instead"); }
      setR({ email: "", new_password: "" });
    } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div className="space-y-6">
      <SectionHead title="Admins & passwords" text="Add other super-admins and reset any user's password." />
      <div className="grid gap-6 xl:grid-cols-2">
        <Panel className="space-y-4">
          <p className="font-display text-xl font-bold uppercase text-white">Super-admins</p>
          <div className="divide-y divide-white/5">
            {admins.map((x) => (
              <div key={x.id} className="flex items-center justify-between py-2" data-testid={`super-admin-row-${x.email}`}>
                <div><p className="text-sm font-semibold text-white">{x.name}</p><p className="text-xs text-slate-500">{x.email}</p></div>
                {x.id !== user.id && <Button size="icon" variant="ghost" className="text-rose-400" onClick={() => remove(x)} data-testid={`remove-admin-${x.email}`}><Trash2 className="h-4 w-4" /></Button>}
              </div>
            ))}
          </div>
          <form onSubmit={add} className="space-y-3 border-t border-white/5 pt-4" data-testid="add-admin-form">
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Email"><Input type="email" required value={a.email} onChange={(e) => setA({ ...a, email: e.target.value })} data-testid="add-admin-email-input" /></Field>
              <Field label="Name"><Input value={a.name} onChange={(e) => setA({ ...a, name: e.target.value })} data-testid="add-admin-name-input" /></Field>
            </div>
            <Field label="Password" hint="Required for new accounts; optional if they already have one"><Input type="password" value={a.password} onChange={(e) => setA({ ...a, password: e.target.value })} data-testid="add-admin-password-input" /></Field>
            <Button type="submit" className="rounded-full font-bold" data-testid="add-admin-submit"><UserPlus className="mr-1 h-4 w-4" /> Add super-admin</Button>
          </form>
        </Panel>
        <Panel className="h-fit space-y-4">
          <p className="font-display text-xl font-bold uppercase text-white">Reset a password</p>
          <form onSubmit={reset} className="space-y-3" data-testid="reset-password-form">
            <Field label="User email"><Input type="email" required value={r.email} onChange={(e) => setR({ ...r, email: e.target.value })} data-testid="reset-user-email-input" /></Field>
            <Field label="New password (optional)" hint="Leave blank to email them a reset link instead"><Input type="password" value={r.new_password} onChange={(e) => setR({ ...r, new_password: e.target.value })} data-testid="reset-user-password-input" /></Field>
            <Button type="submit" variant="outline" className="rounded-full border-white/15 bg-transparent" data-testid="reset-user-submit"><KeyRound className="mr-1 h-4 w-4" /> Reset password</Button>
          </form>
        </Panel>
      </div>
    </div>
  );
}
